import type { CSSObject } from '@mui/material/styles'
import type { Theme } from 'src/Theme'
import type {
  ScrollableElement,
  ScrollbarDisabled,
  DefaultScrollbar,
  VerticalScrollbar,
  HorizontalScrollbar,
} from 'src/types/scrollbars'

import { isFalse, isTrue } from 'src/types/guards'

const scrollbarCustomProps = [
  'withScroll',
  'horizontal',
  'vertical',
]

const isScrollbarDisabled = (props: Partial<ScrollableElement>): props is ScrollbarDisabled => 'withScroll' in props && isFalse(props.withScroll)

const isWithScroll = (props: Partial<ScrollableElement>): props is DefaultScrollbar => 'withScroll' in props && isTrue(props.withScroll)
const isHorizontal = (props: Partial<ScrollableElement>): props is HorizontalScrollbar => 'horizontal' in props && isTrue(props.horizontal) && !isVertical(props)
const isVertical = (props: Partial<ScrollableElement>): props is VerticalScrollbar => 'vertical' in props && isTrue(props.vertical) && !isHorizontal(props)

const scrollBarStyles = (theme: Theme): CSSObject => ({
  scrollbarWidth: 'thin',
  '&.inverted': {
    scrollbarColor: `${theme.colors.border.hover} ${theme.colors.surface.brand}`,
  },
  '&::-webkit-scrollbar': {
    display: 'block',
    width: '0.375rem',
    height: '0.375rem',
    '&.inverted': {
      backgroundColor: theme.colors.surface.brand,
    },
  },

  '&::-webkit-scrollbar-track': {
    '&.inverted': {
      background: theme.colors.surface.brand,
    },
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: theme.sizes.radius.full,
    '&.inverted': {
      backgroundColor: theme.colors.border.hover,
    },
  },
})

type ScrollbarMixinProps = Partial<ScrollableElement> & {
  theme: Theme
}

const scrollbarMixin = ({
  theme,
  ...props
}: ScrollbarMixinProps): CSSObject => {
  if (isScrollbarDisabled(props)) {
    return {
      overflowY: 'hidden',
      overflowX: 'hidden',
    }
  }
  if (isHorizontal(props)) {
    return {
      overflowY: 'hidden',
      overflowX: 'scroll',
      ...scrollBarStyles(theme),
    }
  }
  if (isVertical(props)) {
    return {
      overflowY: 'scroll',
      overflowX: 'hidden',
      ...scrollBarStyles(theme),
    }
  }
  if (isWithScroll(props)) {
    return {
      overflowY: 'auto',
      overflowX: 'auto',
      ...scrollBarStyles(theme),
    }
  }
  return {
    overflowY: 'auto',
    overflowX: 'auto',
  }
}

export {
  isScrollbarDisabled,
  isWithScroll,
  isHorizontal,
  isVertical,
  scrollbarMixin,
  scrollbarCustomProps,
}
